/**
 * The score opened on Arrange or the Lab board, read in the browser before anything is sent.
 *
 * The page draws it at once, and the arranger is sent the text as read, which is what a MIDI
 * file has to be turned into first anyway. The file is read by its first non-space character
 * the way `score-files.ts` reads one: an extension is a hint a user can get wrong, and a `.xml`
 * that is not a score should be refused with words about the score rather than about its name.
 * A zip is opened as compressed MusicXML (`mxl.ts`) and read as the document it names.
 *
 * A MIDI file is written down as a score here, by the editor's own importer, and handed on as
 * the MusicXML that comes out: the arranger reads one format, and the reading of a MIDI file is
 * the same one the editor shows when it opens one. What it could not know -- the bars, the note
 * values, the spelling -- goes with it as a note, so the page can say the original was worked
 * out rather than read.
 *
 * A PDF of printed music is not read here: `arrange-open.ts` sends it to the server, which
 * reads it, and what comes back is picked as MusicXML with a note of its own.
 */

import { toMusicXml } from "@app/score-editor";
import { isMidi, readMidiFile } from "./score-files";
import { isZip, unzipMusicXml } from "./mxl";
import { handedOver } from "./score-opening";

export interface Picked {
  readonly name: string;
  readonly text: string;
  /** What the page should say about how the text was arrived at, when it was not simply read. */
  readonly note: string | null;
}

/**
 * The score another board handed over with the navigation, if it did: Read hands the reading
 * of a scan, under the scan's name and with the note the reading came with.
 */
export function handedPick(state: unknown): Picked | null {
  const text = handedOver(state);
  if (text === null) return null;
  const { name, note } = state as { name?: unknown; note?: unknown };
  return {
    name: typeof name === "string" && name.trim() !== "" ? name : "score.musicxml",
    text,
    note: typeof note === "string" ? note : null,
  };
}

/** The bytes of the file `name` read as a score to arrange, or why they cannot be one. */
export async function pickScore(name: string, data: Uint8Array): Promise<Picked | string> {
  if (isMidi(data)) {
    const midi = readMidiFile(data);
    if (midi.score === null) {
      return `${name} could not be read as MIDI: ${midi.note ?? "no reason given"}`;
    }
    return { name, text: toMusicXml(midi.score), note: midi.note };
  }
  let text: string;
  if (isZip(data)) {
    try {
      text = await unzipMusicXml(data);
    } catch (broken) {
      return `${name} could not be read as compressed MusicXML: ${broken instanceof Error ? broken.message : String(broken)}.`;
    }
  } else {
    text = new TextDecoder().decode(data);
  }
  if (!text.trimStart().startsWith("<")) {
    return `${name} is not MusicXML, MIDI or a PDF. A score to arrange has to be one.`;
  }
  return { name, text, note: null };
}
